/**
 * SaveManager - Game save/load system
 *
 * Manages saving and loading game state to/from localStorage.
 */

import { STORAGE_KEYS } from '../game/config.js';

export class SaveManager {
  constructor() {
    this.saveSlots = 3; // Number of save slots
    this.autoSaveEnabled = true;
    this.autoSaveInterval = 60000; // Auto-save every 60 seconds
    this.autoSaveTimer = null;
  }

  /**
   * Save game state to a specific slot
   * @param {number} slot - Save slot number (0-2)
   * @param {Object} gameState - Game state to save
   * @returns {boolean} Success status
   */
  save(slot, gameState) {
    if (slot < 0 || slot >= this.saveSlots) {
      console.error(`Invalid save slot: ${slot}`);
      return false;
    }

    try {
      const saveData = {
        version: '1.0.0',
        timestamp: Date.now(),
        slot,
        data: gameState
      };

      const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
      localStorage.setItem(key, JSON.stringify(saveData));

      console.log(`Game saved to slot ${slot}`);
      return true;
    } catch (error) {
      console.error('Failed to save game:', error);
      return false;
    }
  }

  /**
   * Load game state from a specific slot
   * @param {number} slot - Save slot number (0-2)
   * @returns {Object|null} Loaded game state or null if not found
   */
  load(slot) {
    if (slot < 0 || slot >= this.saveSlots) {
      console.error(`Invalid save slot: ${slot}`);
      return null;
    }

    try {
      const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
      const saveDataStr = localStorage.getItem(key);

      if (!saveDataStr) {
        console.log(`No save data found in slot ${slot}`);
        return null;
      }

      const saveData = JSON.parse(saveDataStr);

      // Validate save data
      if (!saveData.version || !saveData.data) {
        console.error('Invalid save data format');
        return null;
      }

      // Check version compatibility
      if (saveData.version !== '1.0.0') {
        console.warn(`Save data version mismatch: ${saveData.version}`);
        // Could add migration logic here
      }

      console.log(`Game loaded from slot ${slot}`);
      return saveData.data;
    } catch (error) {
      console.error('Failed to load game:', error);
      return null;
    }
  }

  /**
   * Delete save data from a specific slot
   * @param {number} slot - Save slot number (0-2)
   * @returns {boolean} Success status
   */
  deleteSave(slot) {
    if (slot < 0 || slot >= this.saveSlots) {
      console.error(`Invalid save slot: ${slot}`);
      return false;
    }

    try {
      const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
      localStorage.removeItem(key);
      console.log(`Save slot ${slot} deleted`);
      return true;
    } catch (error) {
      console.error('Failed to delete save:', error);
      return false;
    }
  }

  /**
   * Get metadata for a save slot without loading full data
   * @param {number} slot - Save slot number (0-2)
   * @returns {Object|null} Save metadata or null if empty
   */
  getSaveMetadata(slot) {
    if (slot < 0 || slot >= this.saveSlots) {
      return null;
    }

    try {
      const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
      const saveDataStr = localStorage.getItem(key);

      if (!saveDataStr) {
        return null;
      }

      const saveData = JSON.parse(saveDataStr);

      return {
        slot,
        version: saveData.version,
        timestamp: saveData.timestamp,
        exists: true
      };
    } catch (error) {
      console.error(`Failed to get metadata for slot ${slot}:`, error);
      return null;
    }
  }

  /**
   * Get metadata for all save slots
   * @returns {Array} Array of save metadata objects
   */
  getAllSaveMetadata() {
    const metadata = [];
    for (let i = 0; i < this.saveSlots; i++) {
      metadata.push(this.getSaveMetadata(i) || { slot: i, exists: false });
    }
    return metadata;
  }

  /**
   * Quick save to slot 0
   * @param {Object} gameState - Game state to save
   * @returns {boolean} Success status
   */
  quickSave(gameState) {
    return this.save(0, gameState);
  }

  /**
   * Quick load from slot 0
   * @returns {Object|null} Loaded game state or null
   */
  quickLoad() {
    return this.load(0);
  }

  /**
   * Enable auto-save
   * @param {Function} getGameStateFn - Function that returns current game state
   */
  enableAutoSave(getGameStateFn) {
    if (!this.autoSaveEnabled || this.autoSaveTimer) {
      return;
    }

    this.autoSaveTimer = setInterval(() => {
      try {
        const gameState = getGameStateFn();
        if (gameState) {
          this.save(0, gameState); // Auto-save to slot 0
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, this.autoSaveInterval);

    console.log('Auto-save enabled');
  }

  /**
   * Disable auto-save
   */
  disableAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
      console.log('Auto-save disabled');
    }
  }

  /**
   * Export save data as JSON string for backup
   * @param {number} slot - Save slot number
   * @returns {string|null} JSON string or null
   */
  exportSave(slot) {
    const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
    return localStorage.getItem(key);
  }

  /**
   * Import save data from JSON string
   * @param {number} slot - Save slot number
   * @param {string} jsonData - JSON string to import
   * @returns {boolean} Success status
   */
  importSave(slot, jsonData) {
    if (slot < 0 || slot >= this.saveSlots) {
      console.error(`Invalid save slot: ${slot}`);
      return false;
    }

    try {
      const saveData = JSON.parse(jsonData);

      // Validate format
      if (!saveData.version || !saveData.data) {
        throw new Error('Invalid save data format');
      }

      const key = `${STORAGE_KEYS.SAVE_GAME}_${slot}`;
      localStorage.setItem(key, jsonData);

      console.log(`Save imported to slot ${slot}`);
      return true;
    } catch (error) {
      console.error('Failed to import save:', error);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @returns {boolean} Availability status
   */
  static isAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage information
   * @returns {Object} Storage usage info
   */
  getStorageInfo() {
    let totalSize = 0;
    const saves = {};

    for (let i = 0; i < this.saveSlots; i++) {
      const key = `${STORAGE_KEYS.SAVE_GAME}_${i}`;
      const data = localStorage.getItem(key);
      if (data) {
        const size = new Blob([data]).size;
        saves[`slot${i}`] = size;
        totalSize += size;
      }
    }

    return {
      totalSize,
      saves,
      available: SaveManager.isAvailable()
    };
  }
}
